import React, { Children, cloneElement, FC, PropsWithChildren, ReactElement, useMemo, useState } from 'react';
import { styled } from 'linaria/react';
import { withTheme } from '@shared/lib/theme';
import { segmentedControlBorderRadiusesPx, segmentedControlBorderRadiusesRem, segmentedControlItemBorderRadiusesPx, segmentedControlItemBorderRadiusesRem, segmentedControlSizes } from '@shared/config/components/segmentedControl';
import { SegmentedControlContext } from './SegmentedControlContext';
type SizeValue = keyof typeof segmentedControlSizes;
type ResponsiveSize = SizeValue | {
  mobile: SizeValue;
  desktop: SizeValue;
};
interface SegmentedControlProps {
  size?: ResponsiveSize;
  disabled?: boolean;
}
const SegmentedControl: FC<PropsWithChildren<SegmentedControlProps>> = ({
  size = 'm',
  disabled = false,
  children
}) => {
  const [activeItemIndex, setActiveItemIndex] = useState<number>(0);
  const desktopSize = typeof size === 'object' ? size.desktop || 'm' : size || 'm';
  const mobileSize = typeof size === 'object' ? size.mobile || desktopSize : size || 'm';
  const memoizedProvierProps = useMemo(() => ({
    size: {
      desktop: desktopSize,
      mobile: mobileSize
    },
    activeItemIndex,
    setActiveItemIndex
  }), [size, activeItemIndex, setActiveItemIndex]);
  const childrenWithIndex = Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) {
      return child;
    }
    const isLastElem = index === Children.count(children) - 1;
    return cloneElement(child as ReactElement, {
      index,
      isLastElem
    });
  });
  return <SegmentedControlContext.Provider value={memoizedProvierProps}>
      <SegmentedControlWrapper desktopSize={desktopSize} mobileSize={mobileSize} disabled={disabled}>
        <ActiveItemPlate activeItemIndex={activeItemIndex} desktopSize={desktopSize} mobileSize={mobileSize} itemsCount={Children.count(children)} />
        {childrenWithIndex}
      </SegmentedControlWrapper>
    </SegmentedControlContext.Provider>;
};
export default SegmentedControl;
type SegmentedControlWrapperProps = Required<Pick<SegmentedControlProps, 'disabled'>> & {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
};
const _exp = /*#__PURE__*/() => ({
  theme
}) => theme.colors.background.tertiary;
const _exp2 = /*#__PURE__*/() => ({
  desktopSize
}) => segmentedControlBorderRadiusesRem[desktopSize];
const _exp3 = /*#__PURE__*/() => ({
  disabled
}) => disabled ? '0.5' : '1';
const _exp4 = /*#__PURE__*/() => ({
  disabled
}) => disabled ? 'none' : 'auto';
const _exp5 = /*#__PURE__*/() => ({
  mobileSize
}) => segmentedControlBorderRadiusesPx[mobileSize];
const SegmentedControlWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "SegmentedControlWrapper",
  class: "s14fxxum",
  propsAsIs: false,
  vars: {
    "s14fxxum-0": [_exp()],
    "s14fxxum-1": [_exp2()],
    "s14fxxum-2": [_exp3()],
    "s14fxxum-3": [_exp4()],
    "s14fxxum-4": [_exp5()]
  }
}));
type ActiveItemPlateProps = {
  itemsCount: number;
  activeItemIndex: number;
} & {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
};
const _exp6 = /*#__PURE__*/() => ({
  itemsCount,
  activeItemIndex
}) => `calc((100% - 0.25rem * 2) / ${itemsCount} * ${activeItemIndex} + 0.25rem)`;
const _exp7 = /*#__PURE__*/() => ({
  itemsCount
}) => `calc((100% - 0.25rem * 2) / ${itemsCount})`;
const _exp8 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.background.secondary;
const _exp9 = /*#__PURE__*/() => ({
  desktopSize
}) => segmentedControlItemBorderRadiusesRem[desktopSize];
const _exp0 = /*#__PURE__*/() => ({
  itemsCount,
  activeItemIndex
}) => `calc((100% - 4px * 2) / ${itemsCount} * ${activeItemIndex} + 4px)`;
const _exp1 = /*#__PURE__*/() => ({
  itemsCount
}) => `calc((100% - 4px * 2) / ${itemsCount})`;
const _exp10 = /*#__PURE__*/() => ({
  mobileSize
}) => segmentedControlItemBorderRadiusesPx[mobileSize];
const ActiveItemPlate = withTheme(/*#__PURE__*/styled('div')({
  name: "ActiveItemPlate",
  class: "a10xzmg9",
  propsAsIs: false,
  vars: {
    "a10xzmg9-0": [_exp6()],
    "a10xzmg9-1": [_exp7()],
    "a10xzmg9-2": [_exp8()],
    "a10xzmg9-3": [_exp9()],
    "a10xzmg9-4": [_exp0()],
    "a10xzmg9-5": [_exp1()],
    "a10xzmg9-6": [_exp10()]
  }
}));

require("./SegmentedControl.linaria.module.css!=!../../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./SegmentedControl.tsx");